// Búsqueda de productos para part-search (ProductsSearchPage), el buscador de
// NewProductLine y el asistente. El navegador no habla con Typesense: pide a la
// API de la tienda activa (Product_search en admin-store-api), que busca y
// devuelve sólo lo que este usuario puede ver: sus niveles de precio, nunca
// costos. Los hits y facets conservan la forma de siempre (nkrCode, objectID,
// _highlightResult, { campo: { valor: cantidad } }).
//
// La respuesta llega como string JSON (resultJson) porque httpStore pasa a
// camelCase todas las claves, y aquí las claves son datos: "price_and_stock",
// o un facet como "Brakes > Brake Shoes".
//
// `collection` es la tienda activa: la API sale de ella y aquí sólo separa la
// caché por tienda.

import httpStore from "@/api/httpStore";

const parse = ({ data }) => JSON.parse(data.resultJson);

// Como el cliente de Algolia: la misma búsqueda repetida poco después (borrar
// letras, volver a un filtro) sale de memoria sin llamar al servidor. Se guarda
// la promesa, así que dos búsquedas iguales a la vez también hacen una sola
// llamada; si falla, se olvida para que el siguiente intento vaya al servidor.
// Pocos segundos, porque el stock y el precio cambian.
const CACHE_TTL_MS = 30000;
const CACHE_MAX_ENTRIES = 100;
const responseCache = new Map();

const post = (path, collection, body) => {
  const key = `${collection}|${path}|${JSON.stringify(body)}`;
  const cached = responseCache.get(key);
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
    return cached.promise;
  }
  responseCache.delete(key);
  const promise = httpStore.post(path, body).then(parse);
  promise.catch(() => {
    if (responseCache.get(key)?.promise === promise) responseCache.delete(key);
  });
  responseCache.set(key, { at: Date.now(), promise });
  // Map conserva el orden de inserción: la primera es la más vieja.
  if (responseCache.size > CACHE_MAX_ENTRIES) {
    responseCache.delete(responseCache.keys().next().value);
  }
  return promise;
};

// facetsSelection [{ type, field?, value?, values? }] -> filtros de la API. Una
// selección puede filtrar otro campo (`field`) por varios valores (`values`): el
// vehículo reconocido en lo escrito es un solo chip con todas sus versiones.
const toFilters = selection =>
  selection.map(item => ({
    field: item.field || item.type,
    values: item.values || [item.value]
  }));

/**
 * { hits, facets, found, searchTimeMs, queryInfo }. Cada hit lleva además _matches
 * (dónde coincidió) y _ranking (por qué está ahí).
 *
 * Con page > 1 (scroll infinito) los facets son los de la primera página y vuelven
 * como null. Con `facets: false` (quien sólo lista resultados) tampoco se piden en
 * la primera. `verifyFitment` (ids del vehículo elegido): cada hit lleva _fits,
 * true si su propio fitment incluye alguno.
 */
export const searchProducts = ({
  collection,
  query,
  facetsSelection = [],
  sortBy = "relevance",
  hitsPerPage = 50,
  page = 1,
  verifyFitment = null,
  facets = true
}) =>
  post("/product_search/products", collection, {
    query,
    filters: toFilters(facetsSelection),
    sortBy,
    hitsPerPage,
    page,
    verifyFitment,
    facets
  });

/**
 * Autocompletado: { suggestions: [{ id, code, title, titleHtml, label, html, value,
 * brand, image, available, fits? }], found, searchTimeMs }. `value` es lo que se
 * pone en la búsqueda al elegirla: el código que coincidió o, si coincidió el
 * título, el código NKR.
 */
export const suggestProducts = ({
  collection,
  query,
  limit = 8,
  verifyFitment = null
}) =>
  post("/product_search/suggest", collection, { query, limit, verifyFitment });

// Vocabulario y vehículos cambian poco: se piden una vez cada 10 minutos por
// tienda, no con cada búsqueda.
const SLOW_TTL_MS = 10 * 60 * 1000;
const slowCache = new Map();

const cachedGet = (path, collection) => {
  const key = `${collection}|${path}`;
  const cached = slowCache.get(key);
  if (cached && Date.now() - cached.at < SLOW_TTL_MS) return cached.promise;
  const promise = httpStore.get(path).then(parse);
  promise.catch(() => slowCache.delete(key));
  slowCache.set(key, { at: Date.now(), promise });
  return promise;
};

/** { campo: [valor, ...] } de los campos pedidos, en todo el catálogo. */
export const loadFacetValues = async ({ collection, fields }) => {
  const values = await cachedGet("/product_search/facet_values", collection);
  return Object.fromEntries(fields.map(field => [field, values[field] || []]));
};

/** Todos los vehículos de la tienda. */
export const loadVehicles = collection =>
  cachedGet("/product_search/vehicles", collection);
